import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { Security } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { getSecurityTestChildren } from 'components/SecuritySchema/helpers/getSecurityTestChildren';
import { getTestReferenceKey } from 'components/SecuritySchema/helpers/getTestReferenceKey';
import type {
  ResolvedReferencedTest,
  ResolvedSecurityReference,
  TestReferenceKey,
} from 'components/SecuritySchema/model/ResolvedReferencedTest';
import { assert, type IsExact } from 'conditional-type-checks';
import { array, option } from 'fp-ts';
import { flow, pipe } from 'fp-ts/lib/function';
import { type Draft, produce } from 'immer';

interface ReferencedTestDictionaries {
  local: Security.NamedTestsDictionary;
  objectGroup: Security.NamedTestsDictionary;
}

type ResolveReferenceReturnValue = [
  test: ResolvedSecurityReference,
  key: TestReferenceKey | null,
];

/**
 * As we write the info to the tests "mutably" inside immer drafts, we need a type
 * that allows Security.Test on input, but allows ResolvedReferencedTests fields to be filled.
 */
type ToBeResolvedTest = Security.Test & Partial<ResolvedReferencedTest>;

const resolveReference = (
  node: Draft<ToBeResolvedTest>,
  dictionaries: Draft<ReferencedTestDictionaries>,
  visitedKeys: TestReferenceKey[] = array.empty,
): ResolveReferenceReturnValue => {
  if (Security.isReferencedTest(node)) {
    const resolve = (value: ResolveReferenceReturnValue) => {
      [node.resolvedReference, node.resolvedReferenceKey] = value;
      return value;
    };
    if (node.resolvedReference && node.resolvedReferenceKey !== undefined) {
      // Test was already resolved during recursion through another test.
      return [node.resolvedReference, node.resolvedReferenceKey];
    }
    const testKey = getTestReferenceKey(node.reference);
    if (visitedKeys.includes(testKey)) {
      // Test recursively points to itself (this kind of direct circular reference of ReferencedTests objects
      // means this reference cannot be resolved to any actual non-referenced test,
      // this does not include references to tests with children pointing to some parent).
      return resolve(['circular', null]);
    }
    const targetTest =
      dictionaries[node.reference.dictionary][node.reference.key];
    if (!targetTest) {
      return resolve(['notFound', null]);
    }
    // If this ReferencedTest points to another ReferencedTest, resolve the reference recursively.
    // First non-ReferencedTest is returned from the recursion.
    return resolve(
      resolveReference(targetTest, dictionaries, [...visitedKeys, testKey]),
    );
  }
  return pipe(
    visitedKeys,
    array.last,
    option.fold<TestReferenceKey, ResolveReferenceReturnValue>(
      () => {
        stumble(
          'resolveReference was called on non-reference node while no referenced test was visited yet.',
        );
        return ['notFound', null];
      },
      (key) => [node, key],
    ),
  );
};

const resolveTest = (dictionaries: Draft<ReferencedTestDictionaries>) => {
  const resolveTestForGivenDictionaries = (node: Draft<ToBeResolvedTest>) => {
    if (Security.isReferencedTest(node) && !node.resolvedReference) {
      // If current node is a referenced test, resolve the reference.
      // Do not recursively enter children here, as tests in reference dictionaries
      // are iterated separately.
      resolveReference(node, dictionaries);
    } else {
      // Recurse into the subtree for other node types to find referenced tests.
      getSecurityTestChildren(node).forEach(resolveTestForGivenDictionaries);
    }
  };
  return resolveTestForGivenDictionaries;
};

const resolveDictionaryTests = (
  local?: Draft<Security.NamedTestsDictionary>,
  objectGroup?: Draft<Security.NamedTestsDictionary>,
): void => {
  if (!local) {
    return;
  }
  Object.values(local).map(
    resolveTest({
      local: local ?? {},
      objectGroup: objectGroup ?? {},
    }),
  );
};

const resolveMachineTests = (
  machine: Draft<Security.SecurityMachine> | null,
  objectGroup?: Draft<Security.NamedTestsDictionary>,
): void => {
  if (!machine) {
    return;
  }
  Object.values(machine.ruleChains).map(
    flow(
      prop('rules'),
      array.map(
        flow(
          prop('test'),
          resolveTest({
            local: machine.namedTests ?? {},
            objectGroup: objectGroup ?? {},
          }),
        ),
      ),
    ),
  );
};

// if this assertion starts to fail, it means new security machine should probably
// be added to the list
assert<
  IsExact<Security.SecurityType, 'acl' | 'cloud' | 'nat44' | 'pbr' | 'zoneFw'>
>(true);
const securityMachinesToResolve = [
  'machineAcl',
  'machineNat44',
  'machinePbr',
  'machineZones',
  'machineCloud',
] as const;

export const resolveReferencedTests: (
  security: Security.DeviceSecurity,
) => Security.DeviceSecurity = produce((draft: Security.DeviceSecurity) => {
  resolveDictionaryTests(draft.objects?.namedTests, draft.objects?.namedTests);
  for (const key of securityMachinesToResolve) {
    resolveDictionaryTests(draft[key]?.namedTests, draft.objects?.namedTests);
    resolveMachineTests(draft[key], draft.objects?.namedTests);
  }
});
